import { createComponent, EnvironmentInjector, Injectable, ApplicationRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, take, takeUntil } from 'rxjs';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as FileSaver from 'file-saver';
import * as ExcelJS from 'exceljs';
import { API_ENDPOINTS } from '../constants/apiconstants';
import { HttpService } from '@app/@core/services/http.service';
import * as CryptoJS from 'crypto-es';
import { environment } from '@env/environment';


@Injectable({
  providedIn: 'root'
})
export class CommonService {
  tags: string[] = [];
  private readonly wordArrayKey: any;
  private readonly wordArrayIv: any;
  // Reactive store using BehaviorSubjects
  private userDataSubject = new BehaviorSubject<any>(null);
  private navigationDataSubject = new BehaviorSubject<any>(null);
  private applicationStateSubject = new BehaviorSubject<any>({});
  private queueDataSubject = new BehaviorSubject<any>(null);

  // Public observables
  userData$ = this.userDataSubject.asObservable();
  navigationData$ = this.navigationDataSubject.asObservable();
  applicationState$ = this.applicationStateSubject.asObservable();
  queueData$ = this.queueDataSubject.asObservable();

  constructor(
    private environmentInjector: EnvironmentInjector,
    private http: HttpClient,
    private httpService: HttpService,
    private appRef: ApplicationRef
  ) {
    this.wordArrayIv = CryptoJS.Utf8.parse(environment.crypto_iv);
    this.wordArrayKey = CryptoJS.Utf8.parse(environment.crypto_key);
  }

  getPrimaryIdFromDynamicRoutingUrl(url: string): string | null {
    if (!url) {
      return null;
    }

    const routePath = url.split(/[?#]/)[0];
    const routeSegments = routePath.split('/').filter(Boolean);
    const tabSegmentIndex = routeSegments.indexOf('tab');
    const primaryId = tabSegmentIndex !== -1 ? routeSegments[tabSegmentIndex + 2] : null;

    return primaryId ? decodeURIComponent(primaryId) : null;
  }

  encrypt(text: string): string {
    const encrypted = CryptoJS.AES.encrypt(text, this.wordArrayKey, {
      iv: this.wordArrayIv,
      mode: CryptoJS.CBC,
      padding: CryptoJS.Pkcs7,
    });
    return encrypted.toString(); // Return encrypted text as base64 string
  }

  // Decrypt method (AES-256)
  decrypt(encryptedText: string): string {
    const decrypted = CryptoJS.AES.decrypt(encryptedText, this.wordArrayKey, {
      iv: this.wordArrayIv,
      mode: CryptoJS.CBC,
      padding: CryptoJS.Pkcs7,
    });
    return decrypted.toString(CryptoJS.Utf8);  // Convert back to plaintext (UTF-8)
  }

  /**
   * License number for navigation banner (session navigationData). Handles AES-encrypted values and plain text (e.g. queue rows already decrypted).
   */
  resolveLicenseNumberForBanner(row: Record<string, unknown> | null | undefined): string | null {
    if (!row || typeof row !== 'object') {
      return null;
    }
    const raw = row['license_number'] ?? row['licenseNumber'];
    if (raw == null) {
      return null;
    }
    const s = String(raw).trim();
    if (!s || s === '---') {
      return null;
    }
    try {
      const d = this.decrypt(s);
      const t = d != null ? String(d).trim() : '';
      if (t) {
        return t;
      }
    } catch {
      /* not ciphertext */
    }
    return s;
  }

  setUserData(userData: any): void {
    this.userDataSubject.next(userData);
  }

  getUserData(): Observable<any> {
    return this.userData$;
  }

  getCurrentUserData(): any {
    return this.userDataSubject.value;
  }

  setNavigationData(navData: any): void {
    this.navigationDataSubject.next(navData);
  }

  getNavigationData(): Observable<any> {
    return this.navigationData$;
  }

  getCurrentNavigationData(): any {
    return this.navigationDataSubject.value;
  }

  setApplicationState(state: any): void {
    const currentState = this.applicationStateSubject.value;
    this.applicationStateSubject.next({ ...currentState, ...state });
  }

  getApplicationState(): Observable<any> {
    return this.applicationState$;
  }

  getCurrentApplicationState(): any {
    return this.applicationStateSubject.value;
  }

  setQueueData(queueData: any): void {
    this.queueDataSubject.next(queueData);
  }

  getQueueData(): Observable<any> {
    return this.queueData$;
  }

  getCurrentQueueData(): any {
    return this.queueDataSubject.value;
  }

  // Clear all store data
  clearAllData(): void {
    this.userDataSubject.next(null);
    this.navigationDataSubject.next(null);
    this.applicationStateSubject.next({});
    this.queueDataSubject.next(null);
  }
  async generatePDFFromComponent(applicationId: any) {
    let userId: string | null = null;
    const userObj = this.userDataSubject.value;
    if (userObj) {
      userId = userObj?.id || userObj?.user_id || userObj?.userId || null;
    }
    this.httpService
      .post(API_ENDPOINTS.DOWNLOAD_APPLICATION, { applicationId, userId })
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          if (response?.status === 'success' && response.data) {
            // Pass the full application data object
            this.generatePDF(applicationId, response.data);
            // return response.data;
          } else {
            console.error('No application data returned from API');
          }
        },
        error: err => {
          console.error('Failed to fetch application info', err);
        }
      });
  }
 PDFGenerate( appId: string,) {
    this.http
      .post(`${environment.apiHost}/document/application/${appId}`, {}, { responseType: 'blob' })
      .pipe(take(1))
      .subscribe({
        next: (blob: Blob) => {
          if (!blob || !(blob instanceof Blob) || blob.size === 0) return;
          const url = window.URL.createObjectURL(blob);
          //  const newTab = window.open(url, '_blank');
          const a = document.createElement('a');
          a.href = url;
          a.download = `registration_${appId}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        },
        error: err => {
          console.error('Failed to download PDF', err);
        }
      });
  }

  /** Open application registration PDF in a new browser tab (viewer) instead of downloading. */
  openApplicationPdfInNewTab(appId: string): void {
    if (!appId || String(appId).trim() === '') return;
    this.http
      .post(`${environment.apiHost}/document/application/${appId}`, {}, { responseType: 'blob' })
      .pipe(take(1))
      .subscribe({
        next: (blob: Blob) => {
          if (!blob || !(blob instanceof Blob) || blob.size === 0) return;
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank', 'noopener,noreferrer');
          // Revoke after a short delay so the new tab can load the blob URL
          window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        },
        error: err => {
          console.error('Failed to open PDF', err);
        }
      });
  }
  downloadComplaint(complaintId:string) {
    this.http
      .post(`${environment.apiHost}/complaint/complaint-pdf/${complaintId}`, {}, { responseType: 'blob' })
      .pipe(take(1))
      .subscribe({
        next: (blob: Blob) => {
          if (!blob || !(blob instanceof Blob) || blob.size === 0) return;
          const url = window.URL.createObjectURL(blob);
          //  const newTab = window.open(url, '_blank');
          const a = document.createElement('a');
          a.href = url;
          a.download = `complaint_${complaintId}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        },
        error: err => {
          console.error('Failed to download PDF', err);
        }
      });
  }
async generatePDF(applicationId: any , applicationData:any) {
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-99999px';
    container.style.top = '0';
    container.style.width = '1100px';
    container.id = 'pdf-temp-container';
    document.body.appendChild(container);

    try {
      // Dynamic import to avoid circular dependency
      const { RegistrationTemplateComponent } = await import('@app/Queues/registration-template/registration-template.component');

      const componentRef = createComponent(RegistrationTemplateComponent, {
        environmentInjector: this.environmentInjector,
        elementInjector: this.environmentInjector
      });
      componentRef.instance.applicationData = applicationData;
      container.appendChild(componentRef.location.nativeElement);
      componentRef.changeDetectorRef.detectChanges();
    setTimeout(() => {
        const pdfContent = container.querySelector('#pdf-content');
        if (pdfContent && pdfContent.children.length > 0) {
           this.PDFGenerate(applicationId);
        } else {
          console.error('PDF content element not found or empty - API may have failed');
          console.error('Unable to generate PDF. The application data could not be loaded.');
          // componentRef.destroy();
          if (document.body.contains(container)) {
            document.body.removeChild(container);
          }
          return false;
        }
      }, 8000);
    } catch (error) {
      console.error('Error creating PDF component:', error);
      console.error('An error occurred while generating the PDF.');
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
      return false;
    }
  }



  addCustomTag(tag: string) {
    this.tags.push(tag);
  }

 getQueuesData(data: any, url: string = `../assets/metadata/queues-header.json`){
     return this.http.get<any>(url).pipe(
     map(json => json[data])
   );
   }

  getDataMessage(data: any, url: string = `../assets/metadata/message-center-header.json`){
     return this.http.get<any>(url).pipe(
     map(json => json[data])
   );
   }

  getHeaderData(key: any, url: string){
     return this.http.get<any>(url).pipe(
     map(json => json[key])
   );
   }

  generateAppPDF(applicationId: any) {
    let userId: string | null = null;
    const userObj = this.userDataSubject.value;
    if (userObj) {
      userId = userObj?.id || userObj?.user_id || userObj?.userId || null;
    }
    return this.httpService.post(API_ENDPOINTS.DOWNLOAD_APPLICATION, { applicationId, userId })
  }

getMetaData(data: any, url: string = `../assets/metadata/${data}.json`){
    return this.http.get(url);
  }


  getSystemConfigurationTabs() {
    const url = '../assets/metadata/system-configuration-tabs.json';
    return this.http.get(url);
  }

  getComplaintTabs() {
    const url = '../assets/metadata/complaint-form.json';
    return this.http.get(url);
  }

  getFormatedDate(created_date: any) {
    const currDate = new Date(created_date);
    if (isNaN(currDate.getTime())) {
      return '---';
    }
    const month = (currDate.getMonth() + 1).toString().padStart(2, '0');
    const day = currDate.getDate().toString().padStart(2, '0');
    const year = currDate.getFullYear();
    return `${month}/${day}/${year}`;
  }

  formatDate(dateStr: string): string {
    // Input format: DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD, or ISO string
    if (!dateStr) return '';
    // Special handling for dtFingerprintSubmissionDate: ignore time after 'T'
    if (dateStr.includes('T')) {
      dateStr = dateStr.split('T')[0];
    }
    if (dateStr.includes('/')) {
      const [part1, part2, part3] = dateStr.split('/');
      // MM/DD/YYYY -> YYYY-MM-DD
      return `${part3}-${part1.padStart(2, '0')}-${part2.padStart(2, '0')}`;
    } else if (dateStr.includes('-')) {
      // YYYY-MM-DD -> MM/DD/YYYY
      const [year, month, day] = dateStr.split('-');
      return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
    }
    return dateStr;
  }



  detectIdType(value: string): 'SSN' | 'ITIN' | null {
    const digits = value.replace(/\D/g, '');
    if (!digits) return null;
    return digits.charAt(0) === '9' ? 'ITIN' : 'SSN';
  }

  getLegacyFlag(): boolean {
    const userStr = sessionStorage.getItem('user');
    if (!userStr) return false;
    const user = JSON.parse(userStr);
    return user.additionalFields.is_legacy_user === true;
    // return true
  }

  exportToExcel(rows: any[],columns:any[], sheetName: string ){
        const header = columns.map(col => col.label);
        const data = rows.map(row => columns.map(col => {
          const v = row[col.key] ?? row[col.key.toLowerCase()];
          if (v === null || v === undefined || v === '') {
            return '---';
          }
          if (typeof v === 'number' && isNaN(v)) {
            return '---';
          }
          if (typeof v === 'string' && v.trim().toLowerCase() === 'nan') {
            return '---';
          }
          if (col.dataType === 'date' || v instanceof Date) {
            return this.getFormatedDate(v);
          }
          const result = typeof v === 'object' ? JSON.stringify(v) : String(v);
          return (result === 'NaN' || result === 'null' || result === 'undefined' || result === 'Invalid Date') ? '---' : result;
        }));

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet 1');

        const headerRow = worksheet.addRow(header);
        // headerRow.font = { bold: true };
        // headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
        headerRow.eachCell(cell => {
  cell.font = { bold: true };
  cell.alignment = { vertical: 'middle', horizontal: 'center' };
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'E6F3FD' } // Light blue (ARGB: AARRGGBB)
  };
});

        data.forEach(row => {
          worksheet.addRow(row);
        });

        worksheet.eachRow(row => {
          row.eachCell(cell => {
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
          });
        });

        worksheet.columns.forEach(col => {
          col.width = 30;
        });

        workbook.xlsx.writeBuffer().then(buffer => {
          FileSaver.saveAs(new Blob([buffer]), sheetName);
        });
  }
  /**
   * Generate License PDF for viewing in popup window
   */
  async generateLicensePDFForView(licenseData: any): Promise<void> {
    if (!licenseData || !licenseData.number) {
      console.error('License data is missing or incomplete');
      return;
    }
    await this.generateLicensePDF(licenseData, 'view');
  }

  /**
   * Generate License PDF for downloading to system
   */
  async generateLicensePDFForDownload(licenseData: any): Promise<void> {
    if (!licenseData || !licenseData.number) {
      console.error('License data is missing or incomplete');
      return;
    }
    await this.generateLicensePDF(licenseData, 'download');
  }

  /**
   * Generate License PDF for printing
   */
  async generateLicensePDFForPrint(licenseData: any): Promise<void> {
    if (!licenseData || !licenseData.number) {
      console.error('License data is missing or incomplete');
      return;
    }
    await this.generateLicensePDF(licenseData, 'print');
  }

  /**
   * Main method to generate License PDF
   * Mode options: 'view' (popup), 'download' (file), 'print' (print dialog)
   */
  private async generateLicensePDF(licenseData: any, mode: 'view' | 'download' | 'print'): Promise<void> {
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-99999px';
    container.style.top = '0';
    container.style.width = '1100px';
    container.id = 'license-pdf-temp-container';
    document.body.appendChild(container);

    try {
      // Transform license codes to display values before passing to PDF component
      const transformedLicenseData = this.transformLicenseDataForPDF(licenseData);

      // Dynamic import to avoid circular dependency
      const { LicensePDFTemplateComponent } = await import('@app/shared/components/license-pdf-template/license-pdf-template.component');

      const componentRef = createComponent(LicensePDFTemplateComponent, {
        environmentInjector: this.environmentInjector,
        elementInjector: this.environmentInjector
      });
      componentRef.instance.licenseData = transformedLicenseData;
      container.appendChild(componentRef.location.nativeElement);
      componentRef.changeDetectorRef.detectChanges();

      setTimeout(() => {
        const pdfContent = container.querySelector('#pdf-content');
        if (pdfContent && pdfContent.children.length > 0) {
          const fileName = this.generateLicenseFileName(licenseData.number);
          this.captureLicensePDF(pdfContent as HTMLElement, licenseData.number, fileName, mode, () => {
            componentRef.destroy();
            if (document.body.contains(container)) {
              document.body.removeChild(container);
            }
          });
        } else {
          console.error('License PDF content element not found or empty');
          componentRef.destroy();
          if (document.body.contains(container)) {
            document.body.removeChild(container);
          }
        }
      }, 500);
    } catch (error) {
      console.error('Error creating license PDF component:', error);
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }
  }

  /**
   * Transform license data by converting code values to display values
   * Uses lookups from localStorage (populated by application-dashboard)
   */
  private transformLicenseDataForPDF(licenseData: any): any {
    if (!licenseData) return licenseData;

    const transformedData = { ...licenseData };

    try {
      // Get lookups from localStorage
      const lookupLicenseTypesStr = localStorage.getItem('lookupLicenseTypes');
      const lookupGradeLevelStr = localStorage.getItem('lookupGradeLevel');

      const lookupLicenseTypes: { [key: string]: string } = {};
      const lookupGradeLevel: { [key: string]: string } = {};

      if (lookupLicenseTypesStr) {
        const typesArray = JSON.parse(lookupLicenseTypesStr);
        if (Array.isArray(typesArray)) {
          typesArray.forEach(item => {
            if (item.key && item.value) {
              lookupLicenseTypes[item.key] = item.value;
            }
          });
        }
      }

      if (lookupGradeLevelStr) {
        const gradesArray = JSON.parse(lookupGradeLevelStr);
        if (Array.isArray(gradesArray)) {
          gradesArray.forEach(item => {
            if (item.key && item.value) {
              lookupGradeLevel[item.key] = item.value;
            }
          });
        }
      }

      // Transform type field if it contains a code
      if (transformedData.type && /^\d+$/.test(transformedData.type.toString())) {
        transformedData.type = lookupLicenseTypes[transformedData.type] || transformedData.type;
        console.log('Transformed license type:', transformedData.type);
      }

      // Transform gradeLevel field if it contains a code
      if (transformedData.gradeLevel && /^\d+$/.test(transformedData.gradeLevel.toString())) {
        transformedData.gradeLevel = lookupGradeLevel[transformedData.gradeLevel] || transformedData.gradeLevel;
        console.log('Transformed grade level:', transformedData.gradeLevel);
      }

      return transformedData;
    } catch (error) {
      console.error('Error transforming license data:', error);
      return licenseData;
    }
  }

  /**
   * Generate license PDF filename with license number and current date
   */
  private generateLicenseFileName(licenseNumber: string): string {
    const now = new Date();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const year = now.getFullYear();
    const dateStr = `${month}-${day}-${year}`;
    return `license_${licenseNumber}.pdf`;
  }

  /**
   * Capture License PDF and handle based on mode - Optimized for single page
   */
  private captureLicensePDF(
    element: HTMLElement,
    licenseNumber: string,
    fileName: string,
    mode: 'view' | 'download' | 'print',
    callback: () => void
  ): void {
    // Capture entire element as single canvas for better performance
    html2canvas(element, {
      scale: 1.2,  // Reduced from 2 for faster processing
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      allowTaint: true
    })
      .then(canvas => {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgWidth = 210;  // A4 width in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        const imgData = canvas.toDataURL('image/jpeg', 0.85);  // Reduced quality for speed
        pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);

        const blob = pdf.output('blob');

        // Handle based on mode
        if (mode === 'view') {
          // Open in popup window for viewing
          const blobUrl = URL.createObjectURL(blob);
          const newWin = window.open('', '_blank');
          if (newWin) {
            newWin.document.title = fileName;
            newWin.document.body.innerHTML = `<iframe id="pdfFrame" style="position:fixed;top:0;left:0;width:100%;height:100%;border:0;" src="${blobUrl}"></iframe>`;
          } else {
            console.error('Failed to open new window for PDF');
          }
        } else if (mode === 'download') {
          // Download to system
          FileSaver.saveAs(blob, fileName);
        } else if (mode === 'print') {
          // Open for printing - use window.open with print delay
          const blobUrl = URL.createObjectURL(blob);
          const newWindow = window.open(blobUrl, '_blank');

          if (newWindow) {
            // Wait for the PDF to load in the new window, then trigger print
            setTimeout(() => {
              newWindow.print();
              // Do not close the window automatically - let user close it after printing
            }, 500);
          } else {
            console.error('Failed to open new window for printing');
          }
        }

        callback();
      })
      .catch(err => {
        console.error('License PDF generation error:', err);
        callback();
      });
  }

  toastMessages(){
    return this.httpService.get(API_ENDPOINTS.TOAST_MESSAGES);
  }
  errorMessages(){
    return this.httpService.get(API_ENDPOINTS.MESSAGES);
  }
  nullValueHandler(value: any): any {
    const sortedFiltered = value.sort((a, b) => {
      const sortDateA = a.created_date;
      const sortDateB = b.created_date;
      const dateA = new Date(sortDateA || 0).getTime();
      const dateB = new Date(sortDateB || 0).getTime();
      if (dateB !== dateA) return dateA - dateB;
      const rawA = (a.complaint_form_id ?? a.complaint_formid ?? a.id ?? '').toString();
      const rawB = (b.complaint_form_id ?? b.complaint_formid ?? b.id ?? '').toString();
      const numMatchA = rawA.match(/(\d+)$/);
      const numMatchB = rawB.match(/(\d+)$/);
      if (numMatchA && numMatchB) {
        const numA = Number(numMatchA[1]);
        const numB = Number(numMatchB[1]);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB; // higher numeric suffix first
        }
      }
      if (rawA === rawB) return 0;
      return rawB.localeCompare(rawA);
    });

    const cleanedFiltered = sortedFiltered.map(item => {
      const newItem = { ...item };
      Object.keys(newItem).forEach(key => {
        if (
          newItem[key] === null ||
          newItem[key] === '' ||
          newItem[key] === undefined ||
          newItem[key] === 'null' ||
          (typeof newItem[key] === 'string' && newItem[key].trim() === '')
        ) {
          newItem[key] = '---';
        }
      });
      return newItem;
    });
    return cleanedFiltered;
  }


  loadOtpValidationRules(service: any, destroy$: Subject<void>): Promise<{
    maxAttempts: number;
    maxResendCount: number;
    otpLockoutDuration: number;
    otpResendCooldown: number;
    otpResendLockoutDuration: number;
    resendTimer?: number;
  }> {
    return new Promise((resolve, reject) => {
      const rules = {
        maxAttempts: 3, // Default values
        maxResendCount: 3,
        otpLockoutDuration: 3,
        otpResendCooldown: 60,
        otpResendLockoutDuration: 15,
        resendTimer: 60
      };

      let completedCalls = 0;
      const totalCalls = 5;

      const checkComplete = () => {
        completedCalls++;
        if (completedCalls === totalCalls) {
          resolve(rules);
        }
      };

      // Fetch maximum OTP attempts
      service.getBusinessRuleByKey('Security', 'otp_verification_attempt_limit')
        .pipe(take(1), takeUntil(destroy$))
        .subscribe({
          next: (response: any) => {
            if (response?.data?.data?.intValue) {
              rules.maxAttempts = response.data.data.intValue;
            }
            checkComplete();
          },
          error: (error: any) => {
            console.error('Error fetching maximum OTP attempts rule:', error);
            checkComplete();
          }
        });

      // Fetch maximum OTP resend count
      service.getBusinessRuleByKey('Security', 'otp_resend_attempt_limit')
        .pipe(take(1), takeUntil(destroy$))
        .subscribe({
          next: (response: any) => {
            if (response?.data?.data?.intValue) {
              rules.maxResendCount = response.data.data.intValue;
            }
            checkComplete();
          },
          error: (error: any) => {
            console.error('Error fetching maximum OTP resend count rule:', error);
            checkComplete();
          }
        });

      // Fetch OTP lockout duration
      service.getBusinessRuleByKey('Security', 'verify_button_lock_duration')
        .pipe(take(1), takeUntil(destroy$))
        .subscribe({
          next: (response: any) => {
            if (response?.data?.data?.intValue) {
              rules.otpLockoutDuration = response.data.data.intValue;
            }
            checkComplete();
          },
          error: (error: any) => {
            console.error('Error fetching OTP lockout duration rule:', error);
            checkComplete();
          }
        });

      // Fetch OTP resend cooldown period
      service.getBusinessRuleByKey('Security', 'resend_code_short_disable_duration')
        .pipe(take(1), takeUntil(destroy$))
        .subscribe({
          next: (response: any) => {
            if (response?.data?.data?.intValue) {
              rules.otpResendCooldown = response.data.data.intValue;
              rules.resendTimer = response.data.data.intValue;
            }
            checkComplete();
          },
          error: (error: any) => {
            console.error('Error fetching OTP resend short disable duration rule:', error);
            checkComplete();
          }
        });

      // Fetch OTP resend long lockout duration (after max attempts reached)
      service.getBusinessRuleByKey('Security', 'resend_code_lock_duration')
        .pipe(take(1), takeUntil(destroy$))
        .subscribe({
          next: (response: any) => {
            if (response?.data?.data?.intValue) {
              rules.otpResendLockoutDuration = response.data.data.intValue;
            }
            checkComplete();
          },
          error: (error: any) => {
            console.error('Error fetching OTP resend long lockout duration rule:', error);
            checkComplete();
          }
        });
    });
  }

  async generateReceiptPdfBlob(receiptData: any): Promise<Blob> {
    const pdf = await this.buildReceiptPdfDocument(receiptData);
    return pdf.output('blob');
  }

  async generateReceiptPDF(receiptData: any, mode: 'download' | 'view' | 'print' = 'download'): Promise<void> {
    try {
      const pdf = await this.buildReceiptPdfDocument(receiptData);
      const fileName = `receipt_${receiptData?.referenceNumber || 'payment'}.pdf`;
      if (mode === 'view') {
        const blob = pdf.output('blob');
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      } else if (mode === 'download') {
        pdf.save(fileName);
      } else if (mode === 'print') {
        pdf.autoPrint();
        window.open(pdf.output('bloburl'), '_blank');
      }
    } catch (err) {
      console.error('Receipt PDF generation error:', err);
    }
  }

  private buildReceiptPdfDocument(receiptData: any): Promise<jsPDF> {
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-99999px';
    container.style.top = '0';
    container.style.width = '600px';
    container.id = 'receipt-pdf-temp-container';
    document.body.appendChild(container);

    return new Promise(async (resolve, reject) => {
      try {
        const { ReceiptTemplateComponent } = await import(
          '@app/shared/components/receipt-template/receipt-template.component'
        );
        const componentRef = createComponent(ReceiptTemplateComponent, {
          environmentInjector: this.environmentInjector,
          elementInjector: this.environmentInjector
        });
        componentRef.instance.receiptData = receiptData;
        container.appendChild(componentRef.location.nativeElement);
        componentRef.changeDetectorRef.detectChanges();

        setTimeout(async () => {
          try {
            const canvas = await html2canvas(container, {
              scale: 2,
              useCORS: true,
              backgroundColor: '#ffffff',
              logging: false,
              allowTaint: true
            });
            const pdf = new jsPDF('p', 'mm', 'a4');
            const imgWidth = 210;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            const imgData = canvas.toDataURL('image/jpeg', 0.98);
            pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
            resolve(pdf);
          } catch (err) {
            reject(err);
          } finally {
            if (document.body.contains(container)) {
              document.body.removeChild(container);
            }
          }
        }, 500);
      } catch (error) {
        if (document.body.contains(container)) {
          document.body.removeChild(container);
        }
        reject(error);
      }
    });
  }

}
